import { useState } from 'react';
import { FileTextIcon, PlusIcon, ReceiptIcon } from 'lucide-react';
import { useLatestLoad } from '../../../hooks/useLatestLoad';
import { fetchStockReceipts } from '../../adminApi';
import { EmptyState } from '../../ui/EmptyState';
import { Skeleton } from '../../../ui/Skeleton';
import { btnPrimary, listBox } from '../../ui/styles';
import { LoadFailed } from '../../ui/LoadState';
import { money } from '../../../core/format/currency';
import { dayShort } from '../../../core/format/dates';
import { locationLabel } from '../../../core/rules/stockMovements';
import { ReceiptForm } from './ReceiptForm';
import type { StockControl } from '../useStock';

/** Tabblad Inkoop: geboekte inkoopfacturen, en een nieuwe boeken. */
export function PurchaseReceipts({
  stock
}: {
  stock: StockControl;
}) {
  const {
    data,
    setData,
    status,
    reload
  } = useLatestLoad(fetchStockReceipts);
  const [adding, setAdding] = useState(false);
  const suppliers = [...new Set((data ?? []).map(r => r.supplier))].sort((a, b) => a.localeCompare(b, 'nl'));
  return <div data-id="element-4592" data-mp="3el4tb8dugne3">
      {adding ? <ReceiptForm items={stock.items ?? []} suppliers={suppliers} onCancel={() => setAdding(false)} onDone={receipt => {
      setData(list => [receipt, ...(list ?? [])]);
      setAdding(false);
      // De standen zijn veranderd: opnieuw ophalen zoals de server ze nu rekent.
      stock.reload();
    }} data-id="element-4593" data-mp="3jd63morgc41h" /> : <div className="mb-4 flex flex-wrap items-center justify-between gap-3" data-id="element-4594" data-mp="66mjp2898zsd">
          <p className="text-[13px] text-muted" data-id="element-4595" data-mp="64a1qp2hypfb">Een levering binnen? Boek de factuur: alle artikelen komen in één keer in de voorraad, met de inkoopprijs.</p>
          <button type="button" onClick={() => setAdding(true)} className={btnPrimary} data-id="element-4596" data-mp="3scjdhijhmumq">
            <PlusIcon className="h-4 w-4" strokeWidth={1.75} aria-hidden="true" data-id="element-4597" data-mp="2byjgafc11j36" />
            Inkoopfactuur boeken
          </button>
        </div>}

      {status === 'error' && !data ? <LoadFailed title="De inkoopfacturen laden lukte niet" onRetry={reload} data-id="element-4598" data-mp="ng88yadms3h8" /> : !data ? <div role="status" aria-label="Inkoopfacturen laden" className="space-y-2" data-id="element-4599" data-mp="iglxo6hd4xb2">
          <Skeleton className="h-14 w-full rounded-lg" data-id="element-4600" data-mp="dplwuq54hv3a" />
          <Skeleton className="h-14 w-full rounded-lg" data-id="element-4601" data-mp="do2i53z47s3m" />
        </div> : data.length === 0 ? !adding && <EmptyState icon={ReceiptIcon} title="Nog geen inkoopfacturen" text="Boek je eerste levering; daarna staat hier elke factuur met wat erop stond." data-id="element-4602" data-mp="8p7yjztbpzig" /> : <ul className={listBox} aria-label="Inkoopfacturen" data-id="element-4603" data-mp="3vu74xc0lrouq">
          {data.map(r => {
        const pieces = r.lines.reduce((s, l) => s + l.qty, 0);
        const off = r.invoiceTotalCents !== undefined && Math.abs(r.invoiceTotalCents - r.totalCostCents) > r.lines.length;
        return <li key={r.id} className="px-4 py-3 sm:px-5" data-id="element-4604" data-mp="1p4izj2u8cit4">
                <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1" data-id="element-4605" data-mp="1k5xxcxvf3p21">
                  <span className="min-w-0" data-id="element-4606" data-mp="1rqicuxkud7ld">
                    <span className="text-[14px] text-ink" data-id="element-4607" data-mp="1rnnudble1wul">{r.supplier}</span>
                    <span className="text-[13px] text-muted" data-id="element-4608" data-mp="1wn8rrhkwtx57">
                      {' '}
                      · {r.invoiceNumber} · {dayShort(r.invoiceDate)}
                    </span>
                  </span>
                  <span className="text-[14px] tabular-nums text-ink" data-id="element-4609" data-mp="26dv0cru7lnee">{money(r.totalCostCents)}</span>
                </div>
                <p className="mt-0.5 text-[13px] text-muted" data-id="element-4610" data-mp="18t8px6gb5bzd">
                  {pieces} {pieces === 1 ? 'stuk' : 'stuks'} op {locationLabel[r.location].toLowerCase()}:{' '}
                  {r.lines.slice(0, 3).map(l => `${l.qty}× ${l.name}`).join(', ')}
                  {r.lines.length > 3 && ` en ${r.lines.length - 3} meer`}
                  {off && <span className="text-clay" data-id="element-4611" data-mp="2z3aazghype9s"> · factuurbedrag {money(r.invoiceTotalCents ?? 0)} wijkt af</span>}
                </p>
                {r.file && <p className="mt-1 flex items-center gap-1.5 text-[13px]" data-id="element-4612" data-mp="2wp5f869zwalu">
                    <FileTextIcon className="h-3.5 w-3.5 text-muted" strokeWidth={1.75} aria-hidden="true" data-id="element-4613" data-mp="2so3e1jwipoh8" />
                    {r.file.url ? <a href={r.file.url} target="_blank" rel="noreferrer" className="text-ink underline underline-offset-4 hover:text-clay" data-id="element-4614" data-mp="36kgowa6jqrmq">
                        {r.file.name}
                        <span className="sr-only" data-id="element-4615" data-mp="3flohqcqan09f"> (opent in een nieuw tabblad)</span>
                      </a> : <span className="text-muted" data-id="element-4616" data-mp="3pd1cbc3h84j5">{r.file.name}</span>}
                  </p>}
              </li>;
      })}
        </ul>}
    </div>;
}